/**
 * 订阅逻辑 Hook
 */
import { computed, ref } from 'vue'
import type { Book, MemberTopupSubscribeProduct } from '../configs/types'
import { PRODUCT_LIST_API, PaymentMethodEnum, SUBSCRIBE_API_ENDPOINT } from '../constants'
import {
  createAvailablePaymentMethods,
  createSelectPaymentMethod,
  getPayType,
  getPaymentChannelName,
  initializePaymentMethod,
} from './paymentUtils'
import { api } from '@/api'
import { $formatErrorMessage } from '@/helpers'
import { VIP_TYPE } from '@/constants'
import { SubscriptionCycle } from '@/utils/enum'
import { useLoadingStore, useToastStore } from '@/stores'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { useFbqInitiateCheckout } from '@/composables/fbq'
import { useAxonBeginCheckout } from '@/composables/axon/useAxonBeginCheckout'

export function useSubscription(Scenario: string, book?: Book) {
  const subscribeSuccess = ref(false)
  const loaded = ref(false)
  const error = ref<string | null>(null)
  let successUrl = window.location.href
  const { elementClickForPay } = useElementClick()

  // 充值中心
  if (!book?.contentId)
    successUrl = `${window.location.origin}/member`

  const { $toast } = useToastStore()
  const { $showLoading, $hideLoading } = useLoadingStore()

  // FB 原生埋点（仅 InitiateCheckout）
  const { reportInitiateCheckout } = useFbqInitiateCheckout()

  // Axon 埋点（begin_checkout；purchase 统一由 /paying 验单成功后上报）
  const { reportBeginCheckout } = useAxonBeginCheckout()

  // 多支付方式相关状态
  const currentPaymentMethod = ref<number>(PaymentMethodEnum.PAYPAL) // 默认 PayPal
  const productLists = ref<any[]>([]) // 按支付方式分组的商品列表
  const hasPayPalPasswordlessPayment = ref(false)
  const hasPlacedOrder = ref(false)

  /**
   * 初始化支付方式
   */
  function initPaymentMethod() {
    initializePaymentMethod(
      currentPaymentMethod,
      productLists,
      hasPayPalPasswordlessPayment,
      hasPlacedOrder,
    )
  }

  /**
   * 可用的支付方式列表（订阅页面包含 Stripe）
   */
  const availablePaymentMethods = computed(createAvailablePaymentMethods(
    productLists,
    hasPayPalPasswordlessPayment,
    hasPlacedOrder,
  ))

  /**
   * 当前支付方式的订阅商品列表（有 cycle 且大于 0）
   *
   * 从 productLists 中找到对应支付方式的商品列表，然后过滤出订阅商品
   */
  const currentSubscriptionProducts = computed(() => {
    const currentMethod = Number(currentPaymentMethod.value)

    // 从 productLists 中找到对应支付方式的商品列表
    const productList = availablePaymentMethods.value.find(
      list => Number(list.paymentMethod) === currentMethod,
    )

    if (!productList || !productList.products)
      return []

    return productList.products
  })

  /**
   * subscribeProducts 兼容旧代码，使用 currentSubscriptionProducts
   */
  const subscribeProducts = computed(() => currentSubscriptionProducts.value)

  const getSubscriptList = async function () {
    try {
      const res: any = await api.get(PRODUCT_LIST_API.SUBSCRIBE)

      // 解析多支付方式数据
      if (res.data && typeof res.data === 'object') {
        hasPayPalPasswordlessPayment.value = res.data.hasPayPalPasswordlessPayment || false
        hasPlacedOrder.value = res.data.hasPlacedOrder || false
        productLists.value = res.data.productLists || []

        // 初始化默认支付方式
        initPaymentMethod()
      }
    }
    catch (err) {
      const errorMsg = $formatErrorMessage(err)
      error.value = errorMsg
      $toast(errorMsg)
    }
    loaded.value = true
  }

  const getUnitPrice = function (item: MemberTopupSubscribeProduct) {
    if (!item.cycle)
      return ''

    if (item.cycle === SubscriptionCycle.YEAR)
      return `${(Number(item.price) / 12).toFixed(2)}/month`

    if (item.cycle === SubscriptionCycle.HALF_YEAR)
      return `${(Number(item.price) / 6).toFixed(2)}/month`

    if (item.cycle === SubscriptionCycle.SEASON)
      return `${(Number(item.price) / 3).toFixed(2)}/month`

    if (item.cycle === SubscriptionCycle.MONTH)
      return `${(Number(item.price) / 30).toFixed(2)}/day`

    if (item.cycle === SubscriptionCycle.WEEK)
      return `${(Number(item.price) / 7).toFixed(2)}/day`

    if (item.cycle === SubscriptionCycle.ThreeDays)
      return `${(Number(item.price) / 3).toFixed(2)}/day`

    return ''
  }

  function isZero(val: string | number | null | undefined) {
    if (val === null || val === undefined || val === '')
      return false

    if (Number.parseFloat(val as any) === 0)
      return true

    return false
  }

  function getIsImageBackground(item: any) {
    if (item.subscribeType === VIP_TYPE.DISCOUNT)
      return true

    if (isZero(item.preferentialPrice) || item.preferentialPrice)
      return true

    return false
  }
  const isReloadPage = function () {
    // 无数据时的处理逻辑
    const currentUrl = new URL(window.location.href)
    if (book?.contentId) {
      // 在阅读页面时，移除 isShowpopupRecharge 参数后刷新页面，更新章节解锁状态
      currentUrl.searchParams.delete('isShowpopupRecharge')
      window.location.href = currentUrl.href
    }
    else {
      window.location.reload()
    }
  }

  const onSubscribeProducts = async function (item: MemberTopupSubscribeProduct) {
    elementClickForPay('subscribe', {
      purchaseId: item.subscribeConfigId,
      commodityId: item.productId,
      purchaseType: '2',
      price: item.price,
      paymentChannel: getPaymentChannelName(currentPaymentMethod.value),
      purchaseScenario: Scenario, // store chapter
      vipType: VIP_TYPE.DISCOUNT === item.subscribeType ? '2' : '1',
      vipCycle: item.cycle,
      priceDiscount: item.preferentialPrice,
      vipCycleDiscount: item.preferentialCycle,
    })

    // 创建订单时生成 InitiateCheckout eventId（订阅开启 5 分钟同单去重），随下单接口透传后端
    const subscribeValue = Number(item.preferentialPrice) || Number(item.price) || 0
    const fbEventId = reportInitiateCheckout({
      value: subscribeValue,
      currency: 'USD',
      contentIds: [String(item.productId)],
      contentName: String(item.name || ''),
      contentCategory: 'subscription',
      dedup: true,
    })

    // Axon：下单呼出支付网关时上报 begin_checkout（订阅取优惠价口径；item_name 取商品名）
    const axonPrice = item.preferentialPrice ?? item.price
    const axonItemName = String(item.name || 'Membership')
    reportBeginCheckout({
      item_id: item.productId,
      item_name: axonItemName,
      price: axonPrice,
    })

    $showLoading()
    const url = `${window.location.origin}/paying?redirect_source=${encodeURIComponent(successUrl)}&paymentChannel=${getPaymentChannelName(currentPaymentMethod.value)}&axon_price=${axonPrice}&axon_product_id=${item.productId}&axon_item_name=${encodeURIComponent(axonItemName)}`
    const params = {
      payConfigId: item.subscribeConfigId,
      productId: item.productId,
      memberCurrencyCode: 'usd',
      subscribeConfigId: item.subscribeConfigId,
      contentId: book?.contentId,
      contentChapterId: book?.contentChapterId,
      payType: getPayType(currentPaymentMethod.value),
      type: 2,
      successUrl: `${url}&payment_status=success&type=subscribe&subscribeType=${item.subscribeType}&discountOnceCoins=${item.discountOnceCoins}`,
      cancelUrl: `${url}&payment_status=cancel&type=subscribe&subscribeType=${item.subscribeType}`,
      ...(fbEventId ? { fbEventId } : {}),
    }

    try {
      const { data } = await api.post<any>(SUBSCRIBE_API_ENDPOINT, params)
      subscribeSuccess.value = true
      if (data) {
        // 判断 data 是否为有效的 URL
        try {
          const _ = new URL(data)
          window.location.href = data
        }
        catch {
          isReloadPage()
        }
      }
      else {
        // 无数据时的处理逻辑
        isReloadPage()
      }
    }
    catch (error) {
      $toast($formatErrorMessage(error))
    }
    $hideLoading()
  }

  /**
   * 切换支付方式
   */
  const selectPaymentMethod = createSelectPaymentMethod(currentPaymentMethod)

  getSubscriptList()

  return {
    loaded,
    subscribeProducts,
    subscribeSuccess,
    onSubscribeProducts,
    getUnitPrice,
    getIsImageBackground,
    isZero,
    // 多支付方式相关
    currentPaymentMethod, // 导出当前支付方式
    availablePaymentMethods,
    currentSubscriptionProducts,
    selectPaymentMethod,
    productLists, // 导出原始数据供外部使用
    hasPayPalPasswordlessPayment,
  }
}

// 保持向后兼容的别名导出
export const useSubscript = useSubscription
